import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Form } from '@/components/ui/form';
import { displayToastSuccess } from '@/helpers/display-toast';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';

import { useDeleteDepartment } from '../../hooks/departments/use-delete-department';
import { Department, DepartmentFormData } from '../../types/department';

interface DeleteDepartmentModalProps {
  department: DepartmentFormData | null;
  allDepartments: Department[];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

// Special value for "No department" option
const NO_DEPARTMENT_VALUE = -1;

// Dynamic schema based on whether department has members
// Note: SingleSelect returns string values, so we accept string and transform to number
const createDeleteSchema = (hasMembers: boolean) =>
  z.object({
    replacementDepartmentId: hasMembers
      ? z
          .string({
            required_error:
              'authenticated.settings:pages.departments.deleteModal.replacementRequired',
          })
          .transform(val => parseInt(val, 10))
      : z
          .string()
          .optional()
          .transform(val => (val ? parseInt(val, 10) : undefined)),
  });

type DeleteFormData = z.infer<ReturnType<typeof createDeleteSchema>>;

/**
 * Modal for deleting a department with optional member transfer
 * If department has members, user must select a replacement department
 *
 * @example
 * ```tsx
 * <DeleteDepartmentModal
 *   department={selectedDepartment}
 *   allDepartments={departments}
 *   open={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   onSuccess={() => {
 *     toast.success('Department deleted');
 *     setIsModalOpen(false);
 *   }}
 * />
 * ```
 */
export function DeleteDepartmentModal({
  department,
  allDepartments,
  open,
  onClose,
  onSuccess,
}: DeleteDepartmentModalProps) {
  const { t } = useTranslation('authenticated.settings');
  const deleteDepartmentMutation = useDeleteDepartment();

  const hasMembers = department ? department.memberCount > 0 : false;

  const form = useForm<DeleteFormData>({
    resolver: zodResolver(createDeleteSchema(hasMembers)),
    mode: 'onChange',
    defaultValues: {
      replacementDepartmentId: undefined,
    },
  });

  // Build replacement options with "No department" as first option
  const replacementOptions = [
    {
      value: NO_DEPARTMENT_VALUE.toString(),
      label: t('pages.departments.deleteModal.noDepartment'),
    },
    ...allDepartments
      .filter(dept => dept.id !== department?.departmentId)
      .map(dept => ({
        value: dept.id.toString(),
        label: dept.name,
      })),
  ];

  const handleDelete = async (closeModal: () => void) => {
    if (!department) return;

    const isValid = await form.trigger();
    if (!isValid) return;

    const formData = form.getValues();
    const departmentName = department.name;

    // Build request payload conditionally
    const payload: {
      departmentId: number;
      replacementDepartmentId?: number;
    } = {
      departmentId: department.departmentId,
    };

    // form.getValues() returns the raw string value (pre-transform)
    // So we compare against the string representation of NO_DEPARTMENT_VALUE
    const rawValue = formData.replacementDepartmentId;
    if (rawValue !== undefined && rawValue !== null) {
      const stringValue = String(rawValue);
      if (stringValue !== NO_DEPARTMENT_VALUE.toString()) {
        payload.replacementDepartmentId = parseInt(stringValue, 10);
      }
    }

    try {
      await deleteDepartmentMutation.mutateAsync(payload);
      form.reset();
      closeModal();
      displayToastSuccess(
        t('pages.departments.deleteModal.successDescription', {
          name: departmentName,
        })
      );
      onSuccess?.();
    } catch {
      // Error handled by mutation onError
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const replacementDepartmentId = form.watch('replacementDepartmentId');
  const isFormValid = !hasMembers || !!replacementDepartmentId;

  const messageContent = hasMembers ? (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        <Trans
          i18nKey="authenticated.settings:pages.departments.deleteModal.messageWithMembers"
          values={{ count: department?.memberCount }}
          components={{ strong: <strong className="font-semibold text-foreground" /> }}
        />
      </div>
    </div>
  ) : (
    <div className="text-sm text-muted-foreground">
      {t('pages.departments.deleteModal.messageNoMembers')}
    </div>
  );

  return (
    <ConfirmationModal
      open={open}
      onOpen={() => {}}
      onClose={handleClose}
      title={t('pages.departments.deleteModal.title')}
      message={messageContent}
      confirmText={
        deleteDepartmentMutation.isPending
          ? t('pages.departments.deleteModal.deleting')
          : t('pages.departments.deleteModal.delete')
      }
      confirmVariant="destructive"
      onConfirm={handleDelete}
      isLoading={deleteDepartmentMutation.isPending}
      disabled={!isFormValid}
    >
      {hasMembers && (
        <Form {...form}>
          <form className="space-y-4">
            <SingleSelect
              name="replacementDepartmentId"
              control={form.control}
              options={replacementOptions}
              label={t('pages.departments.deleteModal.replacementLabel')}
              placeholder={t('pages.departments.deleteModal.replacementPlaceholder')}
              searchable={true}
              isClearSelectionEnabled={true}
            />
          </form>
        </Form>
      )}
    </ConfirmationModal>
  );
}
